import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { adminApi } from "../../../api/adminApi";
import Modal from "../../../components/common/modals/Modal";
import "./StoreCompletionReminderModal.css";

const REMINDER_TABS = [
  { id: "store", label: "Store completion" },
  { id: "payment", label: "Payment reminders" },
  { id: "website", label: "Website reminders" },
];

const DEFAULT_TEMPLATES = {
  store: {
    subject: "A friendly reminder to complete your store setup",
    message:
      "Hi {{name}},\n\nYour store setup is waiting for you. Continue where you left off and take the next step toward getting your online store ready.",
  },
  payment: {
    subject: "Payment reminder for request {{requestNumber}}",
    message:
      "Hi {{name}},\n\nA payment is outstanding for {{storeName}} (request {{requestNumber}}).\n\nAmount due: {{amountDue}}\nPayment status: {{paymentStatus}}\nPayment method: {{paymentMethod}}\nPayment reference: {{paymentReference}}\n\nPlease review your invoice and complete payment to continue.",
  },
  website: {
    subject: "A reminder about your website request {{requestNumber}}",
    message:
      "Hi {{name}},\n\nThis is a friendly reminder about your website project for {{storeName}}. Its current status is {{status}}. Please review your request and contact us if you need any help.",
  },
};

const StoreCompletionReminderModal = ({ isOpen, onClose, onSent }) => {
  const [activeType, setActiveType] = useState("store");
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const selectAllRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setRecipients([]);
    setSelectedIds([]);
    setLoading(true);
    setError("");
    adminApi
      .getReminderRecipients(activeType)
      .then((response) => {
        if (!cancelled) setRecipients(response.data || []);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(
            loadError.response?.data?.error ||
              "Could not load reminder recipients.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeType, isOpen]);

  const recipientIds = useMemo(
    () =>
      recipients.map(
        (recipient) =>
          recipient.ReminderID ||
          recipient.UserID ||
          recipient.RequestID,
      ),
    [recipients],
  );
  const allSelected =
    recipientIds.length > 0 && selectedIds.length === recipientIds.length;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        selectedIds.length > 0 && !allSelected;
    }
  }, [allSelected, selectedIds.length]);

  const toggleClient = (userId) => {
    setSelectedIds((current) =>
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId],
    );
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : recipientIds);
  };

  const updateTemplate = (field, value) => {
    setTemplates((current) => ({
      ...current,
      [activeType]: { ...current[activeType], [field]: value },
    }));
  };

  const selectReminderType = (type) => {
    setActiveType(type);
    setSelectedIds([]);
    setError("");
  };

  const handleClose = () => {
    if (sending) return;
    setSelectedIds([]);
    setError("");
    onClose();
  };

  const sendReminders = async (event) => {
    event.preventDefault();
    if (!selectedIds.length) {
      setError("Select at least one client to receive the reminder.");
      return;
    }

    setSending(true);
    setError("");
    try {
      const response = await adminApi.sendReminderEmails(activeType, {
        ...(activeType === "store"
          ? { userIds: selectedIds }
          : { requestIds: selectedIds }),
        ...templates[activeType],
      });
      const result = response.data || { sent: [], failed: [] };
      onSent?.();
      if (result.failed.length) {
        const failedIds = result.failed.map(
          (item) => item.userId || item.requestId,
        );
        setSelectedIds(failedIds);
        toast.warning(
          `Sent ${result.sent.length} reminder(s); ${result.failed.length} failed. Failed recipients remain selected.`,
        );
        return;
      }

      toast.success(response.message || "Reminder emails sent.");
      setSelectedIds([]);
      onClose();
    } catch (sendError) {
      setError(
        sendError.response?.data?.error ||
          "Could not send the selected reminders.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Reminder emails"
      size="lg"
      className="store-reminder-modal"
      closeOnOverlayClick={!sending}
    >
      <form className="store-reminder-form" onSubmit={sendReminders}>
        <p className="store-reminder-intro">
          Select a reminder type, review the matching recipient and invoice
          details, then personalise and send the email.
        </p>

        <div
          className="store-reminder-tabs"
          role="tablist"
          aria-label="Reminder type"
        >
          {REMINDER_TABS.map((tab) => (
            <button
              key={tab.id}
              id={`reminder-tab-${tab.id}`}
              className={`store-reminder-tab ${activeType === tab.id ? "is-active" : ""}`}
              type="button"
              role="tab"
              aria-selected={activeType === tab.id}
              aria-controls="reminder-tab-panel"
              onClick={() => selectReminderType(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="store-reminder-error" role="alert">
            {error}
          </div>
        )}

        <section
          className="store-reminder-recipients"
          id="reminder-tab-panel"
          role="tabpanel"
          aria-labelledby={`reminder-tab-${activeType}`}
          aria-label="Recipients"
        >
          <div className="store-reminder-section-heading">
            <div>
              <h3>Recipients</h3>
              <p>
                {loading
                  ? "Loading client accounts..."
                  : `${recipients.length} ${activeType === "store" ? "client account" : "request"}${recipients.length === 1 ? "" : "s"}`}
              </p>
            </div>
            <label className="store-reminder-select-all">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                disabled={loading || recipients.length === 0}
              />
              Select all
            </label>
          </div>
          <div className="store-reminder-client-list">
            {!loading && recipients.length === 0 && !error && (
              <p className="store-reminder-empty">
                {activeType === "payment"
                  ? "No unpaid requests with an invoice amount were found."
                  : activeType === "website"
                    ? "No active website requests were found."
                    : "No client accounts found."}
              </p>
            )}
            {recipients.map((recipient) => {
              const recipientId =
                recipient.ReminderID ||
                recipient.UserID ||
                recipient.RequestID;
              return (
                <label className="store-reminder-client" key={recipientId}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(recipientId)}
                    onChange={() => toggleClient(recipientId)}
                  />
                  <span className="store-reminder-client-info">
                    <strong>{recipient.FullName || "Client"}</strong>
                    <span>{recipient.Email || "No email address"}</span>
                  </span>
                  <span className="store-reminder-business">
                    {activeType === "store"
                      ? recipient.StoreName ||
                        recipient.PreferredStoreName ||
                        recipient.BusinessName ||
                        "Store name not set"
                      : `${recipient.StoreName || recipient.BusinessName || "Project"} · ${recipient.RequestNumber || ""}`}
                    {activeType === "payment" && (
                      <small className="store-reminder-payment-details">
                        {recipient.Currency || "ZAR"}{" "}
                        {Number(recipient.AmountDue || 0).toFixed(2)} due
                        {" · "}
                        {recipient.PaymentStatus || "Not started"}
                        {" · "}
                        {recipient.PaymentMethod || "No method selected"}
                        {" · Ref "}
                        {recipient.PaymentReference || "Not generated"}
                      </small>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
          <p className="store-reminder-selection-count" aria-live="polite">
            {selectedIds.length} selected
          </p>
        </section>

        <section className="store-reminder-template">
          <div className="store-reminder-section-heading">
            <div>
              <h3>Email template</h3>
              <p>
                Use{" "}
                {(activeType === "payment"
                  ? [
                      "{{name}}",
                      "{{storeName}}",
                      "{{requestNumber}}",
                      "{{amountDue}}",
                      "{{paymentStatus}}",
                      "{{paymentMethod}}",
                      "{{paymentReference}}",
                    ]
                  : ["{{name}}", "{{storeName}}", "{{requestNumber}}", "{{status}}"]
                ).map((token) => (
                  <code key={token}>{token} </code>
                ))}
              </p>
            </div>
          </div>
          <label>
            Subject
            <input
              value={templates[activeType].subject}
              onChange={(event) => updateTemplate("subject", event.target.value)}
              maxLength={180}
              required
            />
          </label>
          <label>
            Message
            <textarea
              value={templates[activeType].message}
              onChange={(event) => updateTemplate("message", event.target.value)}
              rows={5}
              maxLength={4000}
              required
            />
          </label>
          <div className="store-reminder-template-preview">
            <span>EMAIL DESIGN</span>
            <strong>
              {activeType === "payment"
                ? "Payment reminder"
                : activeType === "website"
                  ? "Website project reminder"
                  : "Your store is waiting"}
            </strong>
            <p>
              {activeType === "payment"
                ? "The email includes the outstanding invoice amount, latest payment method and reference, plus a link to pay."
                : activeType === "website"
                  ? "The email includes website request details and a link to review the project."
                  : "Your message will appear in a branded Toora Flex email with a link to continue store setup."}
            </p>
          </div>
        </section>

        <footer className="store-reminder-footer">
          <button
            className="btn-secondary"
            type="button"
            onClick={handleClose}
            disabled={sending}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            type="submit"
            disabled={loading || sending || !selectedIds.length}
          >
            {sending
              ? "Sending reminders..."
              : `Send ${activeType} reminder${selectedIds.length === 1 ? "" : "s"} (${selectedIds.length})`}
          </button>
        </footer>
      </form>
    </Modal>
  );
};

export default StoreCompletionReminderModal;
